import {Component, ElementRef, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {CarBrand, CarType, DamageReport, DamageReportDocument, Employee, Importer, Insurance, JobType, Receiver} from '../../../models/models';
import {Translation} from '../../../models/translation';
import {LoginService} from '../../../services/login.service';
import {DamageAdministrationService} from '../../../services/damage-administration.service';
import {CheckService} from '../../../services/check.service';
import {DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE} from '@angular/material/core';
import {MAT_MOMENT_DATE_ADAPTER_OPTIONS, MomentDateAdapter} from '@angular/material-moment-adapter';
import {MatDatepicker} from '@angular/material/datepicker';
import {DatePipe} from '@angular/common';
import {UntypedFormControl} from '@angular/forms';
import {forkJoin, Observable} from 'rxjs';
import {map, startWith} from 'rxjs/operators';
import * as _moment from 'moment';
import Swal from 'sweetalert2';

const moment = _moment;

export const CustomFormats = {
  parse: {
    dateInput: 'DD.MM.YYYY',
  },
  display: {
    dateInput: 'DD.MM.YYYY',
    monthYearLabel: 'DD.MM.YYYY',
    dateA11yLabel: 'DD.MM.YYYY',
    monthYearA11yLabel: 'DD.MM.YYYY',
  },
};
export class Event {
  isUserInput: boolean;
}

@Component({
  selector: 'app-damage-administration-record',
  templateUrl: './damage-administration-record.component.html',
  styleUrls: ['./damage-administration-record.component.css'],
  providers: [
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS]
    },
    {
      provide: MAT_DATE_FORMATS, useValue: CustomFormats
    },
    [DatePipe]
  ],
})
export class DamageAdministrationRecordComponent implements OnInit {
  @ViewChild('damageFocus', {static: true}) damageDateInput: ElementRef;
  @ViewChild('caseEndFocus', {static: true}) caseEndDateInput: ElementRef;
  @ViewChild('reimbursementFocus', {static: true}) reimbursementDateInput: ElementRef;
  @ViewChild('searchElement', {static: true}) searchElement: ElementRef;
  @ViewChild('docNum', {static: true}) docNum: ElementRef;
  @ViewChild('fileDropRef') fileDropEl: ElementRef;

  public firstTimeLoading = true;
  public translation: Translation;

  public damageReport = new DamageReport();
  public date = new UntypedFormControl(moment());
  public tempDamageDate: Date = new Date();
  public tempCaseEndDate: Date = new Date();
  public tempReimbursementDate: Date = new Date();
  public maxDate: Date = new Date();
  public docNumSelects: number[];
  public docNumControl = new UntypedFormControl();
  public filteredDocNums: Observable<number[]>;
  public insuranceSelects: Insurance[];
  public insuranceControl = new UntypedFormControl();
  public filteredInsurances: Observable<Insurance[]>;
  public jobTypeSelects: JobType[];
  public jobTypeControl = new UntypedFormControl();
  public filteredJobTypes: Observable<JobType[]>;
  public employeeSelects: Employee[];
  public employeeControl = new UntypedFormControl();
  public filteredEmployees: Observable<Employee[]>;
  public senderSelects: Receiver[];
  public senderControl = new UntypedFormControl();
  public filteredSenders: Observable<Receiver[]>;
  public receiverSelects: Receiver[];
  public receiverControl = new UntypedFormControl();
  public filteredReceivers: Observable<Receiver[]>;
  public workshopSelects: Receiver[];
  public workshopControl = new UntypedFormControl();
  public filteredWorkshops: Observable<Receiver[]>;
  public brandSelects: CarBrand[];
  public brandControl = new UntypedFormControl();
  public filteredBrands: Observable<CarBrand[]>;
  public importerSelects: Importer[];
  public importerControl = new UntypedFormControl();
  public filteredImporters: Observable<Importer[]>;
  public typeSelects: CarType[];
  public typeControl = new UntypedFormControl();
  public filteredTypes: Observable<CarType[]>;

  public toast = Swal.mixin({toast: true, position: 'top-right', timer: 2000, timerProgressBar: false, showConfirmButton: false});

  constructor(
    private loginService: LoginService,
    private checkService: CheckService,
    private damageAdministrationService: DamageAdministrationService,
    private datepipe: DatePipe,
    private route: ActivatedRoute,
  ) { }

  async ngOnInit() {
    window.scrollTo(0, 0);
    this.loginService.checkToken();
    this.checkService.checkRight(8);
    this.translation = JSON.parse(localStorage.getItem('Translation'));
    this.load();
  }

  public load() {
    forkJoin([
      this.damageAdministrationService.getDamageReportDocNums(),
      this.damageAdministrationService.getInsurances(),
      this.damageAdministrationService.getJobTypes(),
      this.damageAdministrationService.getEmployees(),
      this.damageAdministrationService.getImporters(),
      this.damageAdministrationService.getCarBrands(),
      this.damageAdministrationService.getCarTypes(),
      this.damageAdministrationService.getReceivers()
    ]).subscribe(([docNumIdResponse, insuranceResponse, jobTypeResponse, employeeResponse, importerResponse, carBrandResponse, carTypeResponse, ReceiverResponse]) => {
      this.docNumSelects = docNumIdResponse;
      this.insuranceSelects = insuranceResponse.Obj;
      this.jobTypeSelects = jobTypeResponse.Obj;
      this.employeeSelects = employeeResponse.Obj;
      this.importerSelects = importerResponse.Obj;
      importerResponse.Obj.forEach(c => { c.ImporterName = this.importerSelects.find(i => i.ImporterId === c.ImporterId).ImporterName; });
      this.brandSelects = carBrandResponse.Obj;
      this.typeSelects = carTypeResponse.Obj;
      this.senderSelects = ReceiverResponse.Obj;
      this.receiverSelects = ReceiverResponse.Obj;
      this.workshopSelects = ReceiverResponse.Obj;
      this.setAutoCompleteFilters();
      if (this.firstTimeLoading && this.route.snapshot.params.reportId !== undefined && this.route.snapshot.params.reportId !== '') {
        this.firstTimeLoading = false;
        const event = new Event();
        event.isUserInput = true;
        this.selectDamageReport(this.route.snapshot.params.reportId, event);
      } else {
        this.initData();
      }
    });
  }

  public initData() {
    this.damageReport.DocNum = 0;
    this.tempDamageDate = new Date();
    this.tempCaseEndDate = new Date();
    this.tempReimbursementDate = null;
    this.damageReport.DamageDate = this.tempDamageDate.toLocaleDateString();
    this.damageReport.CaseEnd = this.tempCaseEndDate.toLocaleDateString();
    this.damageReport.Reimbursement = '';
    this.damageReport.DamageTime = '';
    this.damageReport.Insurance = 0;
    this.damageReport.InsuranceDisplayName = '';
    this.damageReport.JobType = 0;
    this.damageReport.JobTypeDisplayName = '';
    this.damageReport.CausedBy = 0;
    this.damageReport.CausedByDisplayName = '';
    this.damageReport.Sender = 0;
    this.damageReport.SenderDisplayName = '';
    this.damageReport.Workshop = 0;
    this.damageReport.WorkshopDisplayName = '';
    this.damageReport.Brand = 0;
    this.damageReport.BrandDisplayName = '';
    this.damageReport.Type = 0;
    this.damageReport.TypeDisplayName = '';
    this.damageReport.Importer = 0;
    this.damageReport.ImporterDisplayName = '';
    this.damageReport.Receiver = 0;
    this.damageReport.ReceiverDisplayName = '';
    this.damageReport.PolicyNo = '';
    this.damageReport.Identification = '';
    this.damageReport.License = '';
    this.damageReport.ChassisNo = '';
    this.damageReport.CostEstimate = null;
    this.damageReport.DamageAmount = null;
    this.damageReport.InsuranceAmount = null;
    this.damageReport.OwnCosts = null;
    this.damageReport.CheckDocStateDone = false;
    this.damageReport.CheckDocStateRelease = false;
    this.damageReport.CheckDocStateIncomplete = false;
    this.damageReport.InvoiceAmountLow = false;
    this.damageReport.InvoiceAmountOk = false;
    this.damageReport.InvoiceAmountHigh = false;
    this.damageReport.DriverDoor = false;
    this.damageReport.AnotherDoor = false;
    this.damageReport.Roof = false;
    this.damageReport.FrontBumper = false;
    this.damageReport.RearBumper = false;
    this.damageReport.FrontFender = false;
    this.damageReport.RearFender = false;
    this.damageReport.Bonnet = false;
    this.damageReport.RearCover = false;
    this.damageReport.Windshield = false;
    this.damageReport.RearWindow = false;
    this.damageReport.SteeringWheel = false;
    this.damageReport.Rim = false;
    this.damageReport.Tire = false;
    this.damageReport.SideMirror = false;
    this.damageReport.CarSidebar = false;
    this.damageReport.Headlights = false;
    this.damageReport.Taillight = false;
    this.damageReport.Various = false;
    this.damageReport.Underbody = false;
    this.damageReport.Comment = '';
    this.damageReport.InsuranceComment = '';
    this.damageReport.Guilty = false;
    this.damageReport.DamageReportDocuments = [];
  }

  selectDamageReport(id: number, e: Event) {
    // @ts-ignore
    if (!e.isUserInput) { return; }
    if (id !== 0) {
      this.damageAdministrationService.getDamageReportByDocNum(id).subscribe((damageReport: DamageReport) => {
        this.damageReport = damageReport;

        // Fill Select-DisplayNames
        const employeeTestFind = this.employeeSelects.find(o => o.UniqueId === this.damageReport.CausedBy);
        const importerTestFind = this.importerSelects.find(o => o.ImporterId === this.damageReport.Importer);
        const insuranceTestFind = this.insuranceSelects.find(o => o.InsuranceId === this.damageReport.Insurance);
        const jobTypeTestFind = this.jobTypeSelects.find(o => o.JobTypeId === this.damageReport.JobType);
        const receiverTestFind = this.receiverSelects.find(o => o.ReceiverId === this.damageReport.Receiver);
        const senderTestFind = this.senderSelects.find(o => o.ReceiverId === this.damageReport.Sender);
        const workshopTestFind = this.workshopSelects.find(o => o.ReceiverId === this.damageReport.Workshop);
        const brandTestFind = this.brandSelects.find(o => o.CarBrandId === this.damageReport.Brand);
        const typeTestFind = this.typeSelects.find(o => o.CarTypeId === this.damageReport.Type);
        this.damageReport.CausedByDisplayName = employeeTestFind === undefined ? '' : employeeTestFind.LastName;
        this.damageReport.ImporterDisplayName = importerTestFind === undefined ? '' : importerTestFind.ImporterName;
        this.damageReport.InsuranceDisplayName = insuranceTestFind === undefined ? '' : insuranceTestFind.InsuranceName;
        this.damageReport.JobTypeDisplayName = jobTypeTestFind === undefined ? '' : jobTypeTestFind.JobTypeName;
        this.damageReport.ReceiverDisplayName = receiverTestFind === undefined ? '' : receiverTestFind.ReceiverName;
        this.damageReport.SenderDisplayName = senderTestFind === undefined ? '' : senderTestFind.ReceiverName;
        this.damageReport.WorkshopDisplayName = workshopTestFind === undefined ? '' : workshopTestFind.ReceiverName;
        this.damageReport.BrandDisplayName = brandTestFind === undefined ? '' : brandTestFind.CarBrandName;
        this.damageReport.TypeDisplayName = typeTestFind === undefined ? '' : typeTestFind.CarTypeName;

        // Fill dates
        let d;
        if (damageReport.DamageDate !== '') {
          d = damageReport.DamageDate.split('.');
          this.tempDamageDate = new Date(+d[2], +(d[1] - 1), +d[0]);
        } else { this.tempDamageDate = null; }
        if (damageReport.CaseEnd !== '') {
          d = damageReport.CaseEnd.split('.');
          this.tempCaseEndDate = new Date(+d[2], +(d[1] - 1), +d[0]);
        } else { this.tempCaseEndDate = null; }
        if (damageReport.Reimbursement !== '') {
          d = damageReport.Reimbursement.split('.');
          this.tempReimbursementDate = new Date(+d[2], +(d[1] - 1), +d[0]);
        } else { this.tempReimbursementDate = null; }
        this.searchElement.nativeElement.value = '';
        this.docNum.nativeElement.focus();
        this.resetAutoCompleteFilter();
      });
    } else {
      this.load();
    }
  }

  openDamageDatePicker(damagePicker: MatDatepicker<Date>) {
    damagePicker.open();
    setTimeout(() => this.damageDateInput.nativeElement.focus());
  }
  openCaseEndDatePicker(caseEndPicker: MatDatepicker<Date>) {
    caseEndPicker.open();
    setTimeout(() => this.caseEndDateInput.nativeElement.focus());
  }
  openReimbursementDatePicker(reimbursementPicker: MatDatepicker<Date>) {
    reimbursementPicker.open();
    setTimeout(() => this.reimbursementDateInput.nativeElement.focus());
  }
  changeTime($event: Event) {
    // @ts-ignore
    this.damageReport.DamageTime = $event.target.value;
  }

  private setAutoCompleteFilters() {
    this.filteredDocNums = this.docNumControl.valueChanges.pipe(startWith(''), map(value => this._docNumFilter(value)));
    this.filteredInsurances = this.insuranceControl.valueChanges.pipe(startWith(''), map(value => this._insuranceFilter(value)));
    this.filteredJobTypes = this.jobTypeControl.valueChanges.pipe(startWith(''), map(value => this._jobTypeFilter(value)));
    this.filteredEmployees = this.employeeControl.valueChanges.pipe(startWith(''), map(value => this._employeeFilter(value)));
    this.filteredSenders = this.senderControl.valueChanges.pipe(startWith(''), map(value => this._senderFilter(value)));
    this.filteredReceivers = this.receiverControl.valueChanges.pipe(startWith(''), map(value => this._receiverFilter(value)));
    this.filteredWorkshops = this.workshopControl.valueChanges.pipe(startWith(''), map(value => this._workshopFilter(value)));
    this.filteredBrands = this.brandControl.valueChanges.pipe(startWith(''), map(value => this._brandFilter(value)));
    this.filteredTypes = this.typeControl.valueChanges.pipe(startWith(''), map(value => this._typeFilter(value)));
    this.filteredImporters = this.importerControl.valueChanges.pipe(startWith(''), map(value => this._importerFilter(value)));
  }
  private resetAutoCompleteFilter() {
    this.filteredDocNums = this.docNumControl.valueChanges.pipe(startWith(''), map(value => this._docNumFilter(value)));
  }

  private _docNumFilter(value: string): number[] {
    return this.docNumSelects.filter(item => item.toString().includes(value.toString()));
  }
  private _insuranceFilter(value: string): Insurance[] {
    return this.insuranceSelects.filter(item => item.InsuranceName.toString().toLowerCase().includes(value.toString().toLowerCase()));
  }
  private _jobTypeFilter(value: string): JobType[] {
    return this.jobTypeSelects.filter(item => item.JobTypeName.toString().toLowerCase().includes(value.toString().toLowerCase()));
  }
  private _employeeFilter(value: string): Employee[] {
    return this.employeeSelects.filter(item => item.LastName.toString().toLowerCase().includes(value.toString().toLowerCase()));
  }
  private _senderFilter(value: string): Receiver[] {
    return this.senderSelects.filter(item => item.ReceiverName.toString().toLowerCase().includes(value.toString().toLowerCase()));
  }
  private _receiverFilter(value: string): Receiver[] {
    return this.receiverSelects.filter(item => item.ReceiverName.toString().toLowerCase().includes(value.toString().toLowerCase()));
  }
  private _workshopFilter(value: string): Receiver[] {
    return this.workshopSelects.filter(item => item.ReceiverName.toString().toLowerCase().includes(value.toString().toLowerCase()));
  }
  private _brandFilter(value: string): CarBrand[] {
    return this.brandSelects.filter(item => item.CarBrandName.toString().toLowerCase().includes(value.toString().toLowerCase()));
  }
  private _typeFilter(value: string): CarType[] {
    return this.typeSelects.filter(item => item.CarTypeName.toString().toLowerCase().includes(value.toString().toLowerCase()));
  }
  private _importerFilter(value: string): Importer[] {
    return this.importerSelects.filter(item => item.ImporterName.toString().toLowerCase().includes(value.toString().toLowerCase()));
  }

  public displayWithText(type: string) {
    switch (type) {
      case 'receiver': return (s) => s ? s.ReceiverName : s;
      case 'carBrand': return (s) => s ? s.CarBrandName : s;
      case 'carType': return (s) => s ? s.CarTypeName : s;
      case 'importer': return (s) => s ? s.ImporterName : s;
      case 'insurance': return (s) => s ? s.InsuranceName : s;
      case 'jobType': return (s) => s ? s.JobTypeName : s;
      case 'causedBy': return (s) => s ? s.LastName : s;
    }
  }

  selectInsurance(item: Insurance) {
    this.damageReport.Insurance = item.InsuranceId;
    this.damageReport.PolicyNo = item.PoliceNo;
    this.damageReport.InsuranceDisplayName = item.InsuranceName;
  }
  selectJobType(item: JobType) {
    this.damageReport.JobType = item.JobTypeId;
    this.damageReport.JobTypeDisplayName = item.JobTypeName;
  }
  selectEmployee(item: Employee) {
    this.damageReport.CausedBy = item.UniqueId;
    this.damageReport.CausedByDisplayName = item.LastName;
  }
  selectSender(item: Receiver) {
    this.damageReport.Sender = item.ReceiverId;
    this.damageReport.SenderDisplayName = item.ReceiverName;
  }
  selectReceiver(item: Receiver) {
    this.damageReport.Receiver = item.ReceiverId;
    this.damageReport.ReceiverDisplayName = item.ReceiverName;
  }
  selectWorkshop(item: Receiver) {
    this.damageReport.Workshop = item.ReceiverId;
    this.damageReport.WorkshopDisplayName = item.ReceiverName;
  }
  selectType(item: CarType) {
    this.damageReport.Type = item.CarTypeId;
    this.damageReport.TypeDisplayName = item.CarTypeName;
  }
  selectBrand(item: CarBrand) {
    this.damageReport.Brand = item.CarBrandId;
    this.damageReport.BrandDisplayName = item.CarBrandName;
    this.damageAdministrationService.getCarTypesByCarBrandId(item.CarBrandId).subscribe(response => {
      if (response.Ok) {
        this.typeSelects = response.Obj;
        this.filteredTypes = this.typeControl.valueChanges.pipe(startWith(''), map(value => this._typeFilter(value)));
      }
      if (response.Ex) console.log(response.Ex);
      if (response.Msg) this.toast.fire('', response.Msg, response.Ok ? 'success' : 'error');
    }), (error: any) => {
      console.log(error);
      this.toast.fire('Fehler', 'Interner Serverfehler', 'error');
    };
  }
  selectImporter(item: Importer) {
    this.damageReport.Importer = item.ImporterId;
    this.damageReport.ImporterDisplayName = item.ImporterName;
  }

  changeDocState(selection: number) {
    switch (selection) {
      case 0:
        if (this.damageReport.CheckDocStateDone) {
          this.damageReport.CheckDocStateDone = false;
          this.damageReport.CheckDocStateRelease = false;
          this.damageReport.CheckDocStateIncomplete = false;
        } else {
          this.damageReport.CheckDocStateDone = true;
          this.damageReport.CheckDocStateRelease = false;
          this.damageReport.CheckDocStateIncomplete = false;
        }
        break;
      case 1:
        if (this.damageReport.CheckDocStateRelease) {
          this.damageReport.CheckDocStateDone = false;
          this.damageReport.CheckDocStateRelease = false;
          this.damageReport.CheckDocStateIncomplete = false;
        } else {
          this.damageReport.CheckDocStateDone = false;
          this.damageReport.CheckDocStateRelease = true;
          this.damageReport.CheckDocStateIncomplete = false;
        }
        break;
      case 2:
        if (this.damageReport.CheckDocStateIncomplete) {
          this.damageReport.CheckDocStateDone = false;
          this.damageReport.CheckDocStateRelease = false;
          this.damageReport.CheckDocStateIncomplete = false;
        } else {
          this.damageReport.CheckDocStateDone = false;
          this.damageReport.CheckDocStateRelease = false;
          this.damageReport.CheckDocStateIncomplete = true;
        }
        break;
      default:
        return;
    }
  }
  changeInvoiceAmountState(selection: number) {
    switch (selection) {
      case 0:
        if (this.damageReport.InvoiceAmountLow) {
          this.damageReport.InvoiceAmountLow = false;
          this.damageReport.InvoiceAmountOk = false;
          this.damageReport.InvoiceAmountHigh = false;
        } else {
          this.damageReport.InvoiceAmountLow = true;
          this.damageReport.InvoiceAmountOk = false;
          this.damageReport.InvoiceAmountHigh = false;
        }
        break;
      case 1:
        if (this.damageReport.InvoiceAmountOk) {
          this.damageReport.InvoiceAmountLow = false;
          this.damageReport.InvoiceAmountOk = false;
          this.damageReport.InvoiceAmountHigh = false;
        } else {
          this.damageReport.InvoiceAmountLow = false;
          this.damageReport.InvoiceAmountOk = true;
          this.damageReport.InvoiceAmountHigh = false;
        }
        break;
      case 2:
        if (this.damageReport.InvoiceAmountHigh) {
          this.damageReport.InvoiceAmountLow = false;
          this.damageReport.InvoiceAmountOk = false;
          this.damageReport.InvoiceAmountHigh = false;
        } else {
          this.damageReport.InvoiceAmountLow = false;
          this.damageReport.InvoiceAmountOk = false;
          this.damageReport.InvoiceAmountHigh = true;
        }
        break;
      default:
        return;
    }
  }

  save() {
    this.damageReport.DamageDate = this.datepipe.transform(this.tempDamageDate, 'dd.MM.yyyy');
    this.damageReport.CaseEnd = this.datepipe.transform(this.tempCaseEndDate, 'dd.MM.yyyy');
    this.damageReport.Reimbursement = this.tempReimbursementDate !== null ? this.datepipe.transform(this.tempReimbursementDate, 'dd.MM.yyyy') : '';
    if (!this.validate()) {
      return;
    }
    this.damageReport.UserId = +sessionStorage.getItem('UserId');
    this.damageReport.InsertType = false;
    if (this.damageReport.DocNum === 0) {
      this.damageAdministrationService.insertDamageReport(this.damageReport).subscribe((damageReport: DamageReport) => {
        if (damageReport != null) {
          Swal.fire('Erfolg', 'Ihre Schadenmeldung wurde erfolgreich erstellt.', 'success');
          this.damageReport.DocNum = damageReport.DocNum;
          this.docNumSelects.unshift(damageReport.DocNum);
          this.damageReport.DamageReportDocuments = damageReport.DamageReportDocuments;
          this.resetAutoCompleteFilter();
        } else {
          Swal.fire('Fehler', 'Ihre Schadenmeldung konnte nicht erstellt werden.', 'error');
        }
      }, error => Swal.fire('Fehler', 'Ihre Schadenmeldung konnte nicht erstellt werden.', 'error'));
    } else {
      this.damageAdministrationService.updateDamageReport(this.damageReport).subscribe((damageReport: DamageReport) => {
        if (damageReport != null) {
          Swal.fire('Erfolg', 'Ihre Schadenmeldung wurde erfolgreich gespeichert.', 'success');
          this.damageReport.DocNum = damageReport.DocNum;
          this.damageReport.DamageReportDocuments = damageReport.DamageReportDocuments;
        } else {
          Swal.fire('Fehler', 'Ihre Schadenmeldung konnte nicht gespeichert werden.', 'error');
        }
      }, error => Swal.fire('Fehler', 'Ihre Schadenmeldung konnte nicht gespeichert werden.', 'error'));
    }
  }
  delete() {
    if (this.damageReport.DocNum === 0) { return; }
    Swal.fire({
      title: 'Löschen?',
      text: 'Möchten Sie diese Schadenmeldung wirklich löschen: Dokumentnummer: ' + this.damageReport.DocNum + '?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Löschen',
      cancelButtonText: 'Abbrechen',
    }).then((result) => {
      if (result.value) {
        this.damageAdministrationService.deleteDamageReport(this.damageReport.DocNum).subscribe((deleted: boolean) => {
          if (!deleted) {
            Swal.fire('Fehler', 'Die Schadenmeldung konnte nicht gelöscht werden.', 'error');
          } else {
            Swal.fire('Erfolg', 'Die Schadenmeldung wurde erfolgreich gelöscht.', 'success');
          }
        });
      }
    });
  }
  validate() {
    if (this.damageReport.Insurance === 0 || this.damageReport.Insurance === null || this.damageReport.InsuranceDisplayName === '') {
      Swal.fire('Fehler', 'Versicherungsart darf nicht leer sein.', 'error');
      return false;
    }
    if (this.damageReport.JobType === 0 || this.damageReport.JobType === null || this.damageReport.JobTypeDisplayName === '') {
      Swal.fire('Fehler', 'Auftragstyp darf nicht leer sein.', 'error');
      return false;
    }
    if (this.damageReport.CausedBy === 0 || this.damageReport.CausedBy === null || this.damageReport.CausedByDisplayName === '') {
      Swal.fire('Fehler', 'Verursacher darf nicht leer sein.', 'error');
      return false;
    }
    if (this.damageReport.Sender === 0 || this.damageReport.Sender === null || this.damageReport.SenderDisplayName === '') {
      Swal.fire('Fehler', 'Absender darf nicht leer sein.', 'error');
      return false;
    }
    if (this.damageReport.Receiver === 0 || this.damageReport.Receiver === null || this.damageReport.ReceiverDisplayName === '') {
      Swal.fire('Fehler', 'Empfänger darf nicht leer sein.', 'error');
      return false;
    }
    if (this.damageReport.Workshop === 0 || this.damageReport.Workshop === null || this.damageReport.WorkshopDisplayName === '') {
      Swal.fire('Fehler', 'Reparaturwerkstatt darf nicht leer sein. Falls sie noch nicht bekannt ist, geben Sie Unbekannt an.', 'error');
      return false;
    }
    if (this.damageReport.Brand === 0 || this.damageReport.Brand === null || this.damageReport.BrandDisplayName === '') {
      Swal.fire('Fehler', 'Marke darf nicht leer sein.', 'error');
      return false;
    }
    if (this.damageReport.Importer === 0 || this.damageReport.Importer === null || this.damageReport.ImporterDisplayName === '') {
      Swal.fire('Fehler', 'Importeur darf nicht leer sein.', 'error');
      return false;
    }
    if ((this.damageReport.ChassisNo === '' || this.damageReport.ChassisNo === null) &&
      (this.damageReport.License === '' || this.damageReport.License === null) &&
      (this.damageReport.Identification === '' || this.damageReport.Identification === null)) {
      Swal.fire('Fehler', 'Es muss zumindest die Chassis-Nummer, das Kennzeichen oder die Referenz abgefüllt sein.', 'error');
      return false;
    }
    if (this.damageReport.ChassisNo !== '' && this.damageReport.ChassisNo.length !== 17) {
      Swal.fire('Fehler', 'Die Chassis-Nummer muss 17 Zeichen lang sein.', 'error');
      return false;
    }
    if (!this.damageReport.CheckDocStateDone && !this.damageReport.CheckDocStateIncomplete && !this.damageReport.CheckDocStateRelease) {
      Swal.fire('Fehler', 'Dokumentstatus muss gesetzt sein.', 'error');
      return false;
    }
    if (this.damageReport.CheckDocStateDone && (this.damageReport.CaseEnd === '' || this.damageReport.CaseEnd === null || this.damageReport.CaseEnd === undefined)) {
      Swal.fire('Fehler', 'Dossierabschluss darf nicht leer sein.', 'error');
      return false;
    }
    if (this.damageReport.CheckDocStateDone && (this.damageReport.DamageAmount === 0 || this.damageReport.DamageAmount === null)) {
      Swal.fire('Fehler', 'Schadenbetrag darf nicht leer sein.', 'error');
      return false;
    }
    if ((this.damageReport.DamageAmount !== 0 && this.damageReport.DamageAmount !== null) && !this.damageReport.InvoiceAmountHigh && !this.damageReport.InvoiceAmountOk && !this.damageReport.InvoiceAmountLow) {
      Swal.fire('Fehler', 'Rechnung Hoch, OK oder Tief muss gesetzt sein, wenn ein Schadenbetrag angegeben wurde.', 'error');
      return false;
    }
    if (!this.damageReport.CheckDocStateDone && (this.damageReport.DamageAmount !== 0 && this.damageReport.DamageAmount !== null)) {
      Swal.fire('Fehler', 'Die Schadenssumme ist hinterlegt deshalb muss der Status auf Erledigt (grün) gesetzt werden.', 'error');
      return false;
    }
    if (this.damageReport.CheckDocStateDone || this.damageReport.CheckDocStateRelease) {
      if (!this.damageReport.AnotherDoor &&
        !this.damageReport.Bonnet &&
        !this.damageReport.CarSidebar &&
        !this.damageReport.DriverDoor &&
        !this.damageReport.FrontBumper &&
        !this.damageReport.FrontFender &&
        !this.damageReport.Headlights &&
        !this.damageReport.RearBumper &&
        !this.damageReport.RearCover &&
        !this.damageReport.RearFender &&
        !this.damageReport.RearWindow &&
        !this.damageReport.Rim &&
        !this.damageReport.Roof &&
        !this.damageReport.SideMirror &&
        !this.damageReport.SteeringWheel &&
        !this.damageReport.Taillight &&
        !this.damageReport.Tire &&
        !this.damageReport.Underbody &&
        !this.damageReport.Various &&
        !this.damageReport.Windshield) {
        Swal.fire('Fehler', 'Es muss mindestens eine Checkbox unter Schäden ausgewählt werden.', 'error');
        return false;
      }
    }
    return true;
  }

  onFileDropped($event) {
    this.prepareFilesList($event);
  }
  fileBrowserHandler(files) {
    this.prepareFilesList(files);
  }
  deleteFile(index: number) {
    this.damageReport.DamageReportDocuments.splice(index, 1);
  }
  uploadFilesSimulator(index: number) {
    setTimeout(() => {
      if (index === this.damageReport.DamageReportDocuments.length) {
        return;
      } else {
        const progressInterval = setInterval(() => {
          clearInterval(progressInterval);
          this.uploadFilesSimulator(index + 1);
        }, 200);
      }
    }, 1000);
  }
  async prepareFilesList(files: Array<any>) {
    for (const item of files) {
      const reader = new FileReader();
      reader.readAsBinaryString(item);
      const drd = new DamageReportDocument();
      drd.ReportId = 0;
      drd.FileName = item.name;
      drd.ReportId = this.damageReport.DocNum;
      reader.onload = () => {
        drd.Base64String = btoa(reader.result.toString());
        this.damageReport.DamageReportDocuments.push(drd);
      };
    }
    this.fileDropEl.nativeElement.value = '';
    this.uploadFilesSimulator(0);
  }
  formatBytes(bytes, decimals = 2) {
    if (bytes === 0) {
      return '0 Bytes';
    }
    const k = 1024;
    const dm = decimals <= 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  }
  documentDoubleClick(file: DamageReportDocument) {
    const fileType = file.FileName.split('.');
    const binaryString = window.atob(file.Base64String.toString());
    const len = binaryString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; ++i) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    let blob = new Blob();
    switch (fileType[fileType.length - 1].toLowerCase()) {
      case 'pdf':
        blob = new Blob([bytes], {type: 'application/pdf'});
        break;
      case 'doc':
      case 'docx':
        blob = new Blob([bytes], {type: 'application/msword'});
        break;
      case 'xls':
      case 'xlsx':
        blob = new Blob([bytes], {type: 'application/vnd.ms-excel'});
        break;
      case 'png':
        blob = new Blob([bytes], {type: 'image/png'});
        break;
      case 'jpg':
        blob = new Blob([bytes], {type: 'image/jpg'});
        break;
      case 'jpeg':
        blob = new Blob([bytes], {type: 'image/jpeg'});
        break;
      case 'msg':
        blob = new Blob([bytes], {type: 'application/outlook'});
        break;
      default:
        Swal.fire('Fehler', 'Dieses Dateiformat unterstützt keine Vorschau. Bitte wenden Sie sich an Ihren Administrator.', 'error');
        return;
    }
    const fileURL = URL.createObjectURL(blob);
    if (fileType[fileType.length - 1].toLowerCase() === 'msg') {
      const downloadLink = document.createElement('a');
      downloadLink.href = fileURL;
      downloadLink.setAttribute('download', file.FileName);
      document.body.appendChild(downloadLink);
      downloadLink.click();
      return;
    }
    window.open(fileURL, '_blank');
  }

  calculateOwnCosts() {
    if (this.damageReport.DamageAmount !== null && this.damageReport.InsuranceAmount !== null) {
      this.damageReport.OwnCosts = this.damageReport.DamageAmount - this.damageReport.InsuranceAmount;
    }
  }
}
